import type { FC } from 'react';
import { Icon } from '@repo/ui/Icon';
import { type IconNames } from '@repo/ui/Icon/utils';
import { Flex } from '@repo/ui/Flex';
import { Text } from '../../Language/Language';
import s from './BucketHeader.module.css';

export type BucketType =
  | 'available'
  | 'claimHistory'
  | 'ineligible'
  | 'notYetDelivered';

interface BucketHeaderProps {
  /** The type of bucket, determines icon and default label */
  bucketType: BucketType;
  /** Optional tracking number to display (for shipment-based buckets) */
  trackingNumber?: string;
  /** Optional date to display (e.g., delivery date) */
  date?: string;
  /** Custom language key for the header text */
  labelLangKey?: string;
  /** Replacement tokens for the label */
  replacementTokens?: Record<string, string>;
}

const bucketConfig: Record<
  BucketType,
  { icon: IconNames; defaultLangKey: string }
> = {
  available: {
    icon: 'package',
    defaultLangKey: 'Global.itemSelection.bucket.available',
  },
  claimHistory: {
    icon: 'refresh',
    defaultLangKey: 'Global.itemSelection.bucket.claimHistory',
  },
  ineligible: {
    icon: 'blocked',
    defaultLangKey: 'Global.itemSelection.bucket.ineligible',
  },
  notYetDelivered: {
    icon: 'truck',
    defaultLangKey: 'Global.itemSelection.bucket.notYetDelivered',
  },
};

/**
 * Header component for item buckets with icon and optional tracking info.
 * Used for grouping items in the "Available for claim" and "Unavailable for claim" sections.
 * Each bucket type has a distinct icon and label.
 */
export const BucketHeader: FC<BucketHeaderProps> = ({
  bucketType,
  trackingNumber,
  date,
  labelLangKey,
  replacementTokens = {},
}) => {
  const config = bucketConfig[bucketType];
  const langKey = labelLangKey ?? config.defaultLangKey;

  // Build replacement tokens, including date if provided
  const tokens = {
    ...replacementTokens,
    ...(date && { date }),
  };

  return (
    <Flex direction="column" gap="g-4" className={s.bucketHeader}>
      <Flex alignItems="center" gap="g-8" className={s.titleRow}>
        <Icon name={config.icon} size="sm" className={s.icon} />
        <span className={s.title}>
          <Text langKey={langKey} replacementTokens={tokens} />
        </span>
      </Flex>
      {trackingNumber && (
        <span className={s.trackingNumber}>
          <Text
            langKey="Global.itemSelection.bucket.trackingNumber"
            replacementTokens={{ trackingNumber }}
          />
        </span>
      )}
    </Flex>
  );
};
